import { useEffect, useMemo, useState } from 'react';

import Button from '@/components/atoms/Button';
import ExternalLink from '@/components/atoms/ExternalLink';
import Heading from '@/components/atoms/Heading';
import IntroducerRewardsPopup from '@/components/organisms/IntroducerRewardsPopup';
import useCallAPIGetIntroduceInfo from '@/hooks/api/useCallAPIGetIntroduceInfo';
import introduceInfoState from '@/states/introduceInfoState';
import url from '@/utils/config';
import { Constants } from '@/utils/constants';
import { useRecoilValue } from 'recoil';

type Props = {
  anchor: string;
};

/**
 * お友達紹介クーポン
 *
 * @param {Props} { anchor }
 * @return お友達紹介クーポンコンポーネント
 */
const IntroducerRewards = ({ anchor }: Props) => {
  /** 取得したデータ */
  const introduceInfo = useRecoilValue(introduceInfoState);

  /** APIフック */
  const { execute: callAPIGetIntroduceInfo } = useCallAPIGetIntroduceInfo();

  /** モーダルフラグ */
  const [isOpen, setIsOpen] = useState(false);

  useEffect((): void => {
    if (isOpen) return;
    callAPIGetIntroduceInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  /** twitterテキスト */
  const twitterText = useMemo(() => {
    if (!introduceInfo.promotion_code) return '';
    return Constants.IntroducerRewards.SNS_TEXT(
      Constants.IntroducerRewards.SnsType.TWITTER,
      introduceInfo.promotion_code
    );
  }, [introduceInfo]);

  /** mailテキスト */
  const mailText = useMemo(() => {
    if (!introduceInfo.promotion_code) return '';
    return Constants.IntroducerRewards.SNS_TEXT(
      Constants.IntroducerRewards.SnsType.MAILTO,
      introduceInfo.promotion_code
    );
  }, [introduceInfo]);

  /** lineテキスト */
  const lineText = useMemo(() => {
    if (!introduceInfo.promotion_code) return '';
    return Constants.IntroducerRewards.SNS_TEXT(
      Constants.IntroducerRewards.SnsType.LINE,
      introduceInfo.promotion_code
    );
  }, [introduceInfo]);

  /** クーポン発行（モーダルを開く） */
  const onClickCreateCoupon = () => {
    if (!introduceInfo.is_promotion_code_apply) return;
    setIsOpen(true);
  };

  /** クーポンをコピー */
  const onClickCopyCoupon = async () => {
    if (!introduceInfo.promotion_code) return;
    await navigator.clipboard.writeText(introduceInfo.promotion_code);
  };

  /** モーダルを閉じる */
  const onClickClose = () => {
    setIsOpen(false);
  };

  return (
    <div className='m-box-02' id={anchor}>
      <IntroducerRewardsPopup isOpen={isOpen} onClose={onClickClose} />
      <div className='m-box-02__inner'>
        <Heading as='h2' icon='friend'>
          お友達紹介
        </Heading>
        <p className='m-text'>お友達紹介クーポンと一緒に「#NURO光体験レポート」をシェアしよう！</p>
        <p className='m-linkTxt'>
          <ExternalLink href='https://www.nuro.jp/hikari/friend_coupon'>
            お友達紹介クーポンの詳細はこちら
          </ExternalLink>
        </p>

        <div className='m-box align-center'>
          <div className='m-box__inner'>
            <h4 className='m-heading-l4'>
              <span className='m-heading-l4__hdg'>ご紹介用クーポンコード</span>
            </h4>
            {/* クーポンコードがなければ発行するボタンを表示 */}
            {!introduceInfo.promotion_code && (
              <div className='m-button align-center'>
                <Button
                  variant='secondary'
                  onClick={onClickCreateCoupon}
                  // 発行不可ならdisabled
                  disabled={!introduceInfo.is_promotion_code_apply}
                  ariaLabel='ご紹介用クーポンコードを発行する'
                >
                  発行する
                </Button>
              </div>
            )}
            {/* クーポンコードがあれば表示 */}
            {introduceInfo.promotion_code && (
              <div data-testid='promotion-code-box'>
                <div className='m-form__lyt'>
                  <input
                    type='text'
                    className='m-form-text align-center'
                    value={introduceInfo.promotion_code}
                    readOnly
                  />
                </div>
                <div className='m-button m-button--sec align-center'>
                  <Button
                    variant='secondary'
                    onClick={onClickCopyCoupon}
                    ariaLabel='ご紹介クーポーンコードをコピーする'
                  >
                    コピーする
                  </Button>
                </div>
                <h4 className='m-heading-l4'>
                  <span className='m-heading-l4__hdg'>お友達にシェア！</span>
                </h4>
                <div className='m-box-sns'>
                  <div className='m-box-sns__inner'>
                    <div className='twitter'>
                      <a
                        className='sns-link-label'
                        href={twitterText}
                        target='_blank'
                        rel='noreferrer'
                      >
                        <img src={url('/images/logo-white.png')} width='25' alt='X（旧:twitter）' />
                      </a>
                    </div>
                    <div className='mail'>
                      <a href={mailText} target='_blank' rel='noreferrer'>
                        <img src={url('/images/icon_mail.png')} width='48' alt='mail' />
                      </a>
                    </div>
                    <div className='line'>
                      <a href={lineText} target='_blank' rel='noreferrer'>
                        <img src={url('/images/icon_line.png')} width='48' alt='line' />
                      </a>
                    </div>
                  </div>
                </div>
                <h4 className='m-heading-l4'>
                  <span className='m-heading-l4__hdg'>クーポンコード有効期限</span>
                </h4>
                <div className='align-center'>
                  {introduceInfo.promotion_code_expires}
                  {/* 有効期限切れエラーメッセージ */}
                  {introduceInfo.is_promotion_code_expired && (
                    <strong> {Constants.IntroducerRewards.PROMOTION_CODE_EXPIRED_ERROR}</strong>
                  )}
                </div>
                <h4 className='m-heading-l4'>
                  <span className='m-heading-l4__hdg'>現在の申込件数</span>
                </h4>
                <div className='align-center'>{introduceInfo.introduce_count}件</div>
              </div>
            )}
          </div>
        </div>
        {/* お知らせ */}
        <h3 className='m-heading-l3'>
          <span className='m-heading-l3__hdg'>お知らせ</span>
        </h3>
        <p className='m-text'>
          クーポンコードの有効期限は10年間となります。有効期限が過ぎた場合、クーポンコードのご利用はできません。
          <br />
          なお、お手続き前にお申し込みされたご紹介先のお客さまについては、キャッシュバックの適用は致しかねますので予めご了承ください。
          <br />
          クーポンコード有効期限内であっても、以下の場合はクーポンコードのご利用がいただけません。
        </p>
        <ul className='m-list m-text-notice'>
          <li className='m-list__item'>ご利用上限回数に到達した場合</li>
          <li className='m-list__item'>お客さまのNURO 光のご利用が停止した場合</li>
          <li className='m-list__item'>その他クーポン規定にて別途定める条件となった場合</li>
        </ul>
      </div>
    </div>
  );
};

export default IntroducerRewards;
